import React, { useEffect, useState } from "react";
import { TextField, IconButton, Typography, Dialog, DialogTitle, DialogContent, Divider, Chip} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Image from "mui-image";
import { fetchWitnessHistory } from "./fetchHandler";
import SignatureCanvas from "../../utils/commonComponent/SignatureCanvas";
import { AddButton } from "../../utils/commonComponent/Custom/Button";

const EMPTY_INPUT = { name: "", designation: "", companyName: "", Sign: "" };

const ObservationWitnessHistory = ({ observationSheetId, onWitnessChange, permissions}) => {
  const [witnessRows, setWitnessRows] = useState([]); // existing rows from DB
  const [deletedIds, setDeletedIds] = useState([]);
  const [newRows, setNewRows] = useState([]); // newly added rows (not yet in DB)
  const [editedRows, setEditedRows] = useState({}); // edited rows: { id/tempId: updatedData }
  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // { id, isNew, _tempId }
  const [inputStates, setInputStates] = useState(EMPTY_INPUT);

  // Fetch existing witness rows from DB
  const fetchWitnessData = async () => {
    try {
      const result = await fetchWitnessHistory(observationSheetId);
      setWitnessRows(result || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchWitnessData();
  }, [observationSheetId]);

  // ─── Notify parent on any pending change
  useEffect(() => {
    onWitnessChange?.({ newRows, deletedIds, editedRows });
  }, [newRows, deletedIds, editedRows]);

  // ─── Open modal
  const openAddModal = () => {
    setEditTarget(null);
    setInputStates(EMPTY_INPUT);
    setOpen(true);
  };

  const openEditModal = (row) => {
    setEditTarget({ id: row.id, isNew: row._isNew, _tempId: row._tempId });
    setInputStates({
      name: row.name || "",
      designation: row.designation || "",
      companyName: row.companyName || "",
      Sign: row.Sign || "",
    });
    setOpen(true);
  };

  // ─── Add
  const addLocalWitness = () => {
    const tempRow = {
      _tempId: Date.now(),
      ...inputStates,
    };
    setNewRows((prev) => [...prev, tempRow]);
    setInputStates(EMPTY_INPUT);
    setOpen(false);
  };

  // ─── Edit (save locally)
  const saveEditLocally = () => {
    if (editTarget.isNew) {
      // Update inside newRows array
      setNewRows((prev) =>
        prev.map((r) =>
          r._tempId === editTarget._tempId ? { ...r, ...inputStates } : r
        )
      );
    } else {
      // Mark existing DB row as edited — will PATCH on handleSubmit
      setEditedRows((prev) => ({
        ...prev,
        [editTarget.id]: { ...inputStates },
      }));
      // Also update display in witnessRows
      setWitnessRows((prev) =>
        prev.map((r) => (r.id === editTarget.id ? { ...r, ...inputStates } : r))
      );
    }
    setInputStates(EMPTY_INPUT);
    setEditTarget(null);
    setOpen(false);
  };

  // ─── Delete
  const removeRow = (id, isNew) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this witness?");
    if (!confirmDelete) return;

    if (isNew) {
      setNewRows((prev) => prev.filter((r) => r._tempId !== id));
    } else {
      setDeletedIds((prev) => [...prev, id]);
      setWitnessRows((prev) => prev.filter((r) => r.id !== id));
      // Also clean up editedRows if it was edited before deleting
      setEditedRows((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  //  Merged rows for display
  const allRows = [
    ...witnessRows,
    ...newRows.map((r) => ({ ...r, id: r._tempId, _isNew: true })),
  ];

  // ─── Modal submit handler
  const handleModalSubmit = () => {
    // Simple mandatory validation
    if (!inputStates.name.trim() || !inputStates.designation.trim() || !inputStates.companyName.trim()) {
      alert("Please fill all mandatory fields.");
      return;
    }
    if (editTarget) {
      saveEditLocally();
    } else {
      addLocalWitness();
    }
  };

  return (
    <div className="mt-5">
      <Divider sx={{ my: 1 }}>
        <Chip
          label={`Witness Details`}
          variant="outlined"
          sx={{ fontSize: "16px", fontWeight: "bold" }}
        />
      </Divider>
      <div className="flex justify-between items-center mb-3">
        <Typography variant="h6"></Typography>
        <AddButton
          id="btn-add-outline"
          label="Add Witness"
          onClick={openAddModal}
          icon={<AddIcon fontSize="small" />}
        />
      </div>

      <div className="overflow-auto">
        <table className="min-w-full border text-sm">
          <thead className="bg-blue-50">
            <tr>
              <th className="border px-3 py-2 text-center">Sr No</th>
              <th className="border px-3 py-2 text-center">Name</th>
              <th className="border px-3 py-2 text-center">Designation</th>
              <th className="border px-3 py-2 text-center">Company Name</th>
              <th className="border px-3 py-2 text-center">Sign</th>
              {permissions.edit && permissions.delete && permissions.allAccess && (
                  <th className="border px-3 py-2 text-center">Action</th>
              )}
            </tr>
          </thead>
          <tbody>
            {allRows.map((row, index) => (
              <tr key={row._isNew ? `new-${row._tempId}` : row.id}>
                <td className="border px-3 py-2 text-center">{index + 1}</td>
                <td className="border px-3 py-2 text-center">{row.name || "-"}</td>
                <td className="border px-3 py-2 text-center">{row.designation || "-"}</td>
                <td className="border px-3 py-2 text-center">{row.companyName || "-"}</td>
                <td className="border px-3 py-2 text-center">
                  {row.Sign ? (
                    <Image src={row.Sign} width={120} height={60} />
                  ) : (
                    "-"
                  )}
                </td>
                <td className="border px-3 py-2 text-center">
                  {permissions.edit && permissions.allAccess && (
                    <IconButton onClick={() => openEditModal(row)}>
                      <EditIcon color="primary" />
                    </IconButton>
                  )}
                  {permissions.delete && permissions.allAccess && (
                    <IconButton
                      onClick={() =>
                        row._isNew
                          ? removeRow(row._tempId, true)
                          : removeRow(row.id, false)
                      }
                    >
                      <DeleteIcon color="error" />
                    </IconButton>
                  )}
                </td>
              </tr>
            ))}

            {allRows.length === 0 && (
              <tr>
                <td colSpan={6} className="border px-3 py-3 text-center">
                  No Witness Added
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="flex justify-between items-center border-b px-6 py-4">
          <span className="text-lg font-semibold text-gray-800">
            {editTarget ? "Edit Witness" : "Add Witness"}
          </span>
          <IconButton
            onClick={() => setOpen(false)}
            size="small"
            className="!bg-gray-100 hover:!bg-red-100 hover:!text-red-500 transition-all duration-200"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <TextField
              label="Name"
              size="small"
              value={inputStates.name}
              onChange={(e) =>
                setInputStates({ ...inputStates, name: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Designation"
              size="small"
              value={inputStates.designation}
              onChange={(e) =>
                setInputStates({ ...inputStates, designation: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Company Name"
              size="small"
              value={inputStates.companyName}
              onChange={(e) =>
                setInputStates({ ...inputStates, companyName: e.target.value })
              }
              fullWidth
            />
          </div>

          <div className="mt-5">
            <Typography className="mb-2">Signature</Typography>
            <SignatureCanvas
              url={inputStates.Sign}
              updateUrl={(url) => setInputStates({ ...inputStates, Sign: url })}
            />
          </div>

          <div className="mt-5 flex justify-end">
            <button
              onClick={handleModalSubmit}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              {editTarget ? "Update" : "Add +"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ObservationWitnessHistory;